// 📄 pages/Admin/sections/TransactionCatalogSection.tsx
import { useEffect, useRef, useState } from 'react';
import { Plus, RefreshCw, Terminal, Sparkles } from 'lucide-react';
import { TransaccionSap, getTransacciones, deleteTransaccion, seedCatalogoTransacciones } from '../../../services/catalogoTransaccionesService.ts';
import TransactionCatalogGrid from '../../../components/abap/TransactionCatalogGrid.tsx';
import TransactionFormModal from '../components/TransactionFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

export default function TransactionCatalogSection() {
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTransaccion, setEditingTransaccion] = useState<TransaccionSap | null>(null);
  const [transaccionAEliminar, setTransaccionAEliminar] = useState<TransaccionSap | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El Admin ya tiene permiso de escritura garantizado (ruta gateada por
  // AdminRoute), así que acá el auto-seed es siempre seguro. Ver mismo
  // criterio en [[BadiCatalogSection.tsx]].
  const seedIntentado = useRef(false);

  const cargar = async () => {
    setLoading(true);
    try {
      let data = await getTransacciones();
      if (data.length === 0 && !seedIntentado.current) {
        seedIntentado.current = true;
        setSembrando(true);
        try {
          await seedCatalogoTransacciones();
          data = await getTransacciones();
        } finally {
          setSembrando(false);
        }
      }
      setTransacciones(data);
    } catch (err) {
      console.error('Error cargando el catálogo de transacciones:', err);
      setError('No se pudo cargar el catálogo de transacciones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditingTransaccion(null); setModalOpen(true); };
  const openEdit = (t: TransaccionSap) => { setEditingTransaccion(t); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!transaccionAEliminar) return;
    setEliminando(true);
    try {
      await deleteTransaccion(transaccionAEliminar.id);
      setTransaccionAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar la transacción:', err);
    } finally {
      setEliminando(false);
    }
  };

  const handleSeed = async () => {
    setSembrando(true);
    setError(null);
    try {
      await seedCatalogoTransacciones();
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al cargar los datos de ejemplo.');
    } finally {
      setSembrando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Catálogo de Transacciones SAP</h1>
          <p className="text-sm text-gray-400 mt-1">Guía y diccionario rápido de transacciones de desarrollo, monitoreo, compras, configuración, transportes y seguridad.</p>
        </div>
        <div className="flex items-center gap-2">
          {transacciones.length === 0 && !loading && (
            <button
              onClick={handleSeed}
              disabled={sembrando}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-300 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {sembrando ? 'Cargando...' : 'Cargar datos de ejemplo'}
            </button>
          )}
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Nueva Transacción
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
        </div>
      ) : transacciones.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay transacciones registradas en el catálogo.</p>
        </div>
      ) : (
        <TransactionCatalogGrid transacciones={transacciones} onEdit={openEdit} onDelete={setTransaccionAEliminar} />
      )}

      {modalOpen && (
        <TransactionFormModal transaccion={editingTransaccion} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {transaccionAEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta transacción del catálogo?"
          message={`"${transaccionAEliminar.transaccion} - ${transaccionAEliminar.nombre}" se eliminará del catálogo global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setTransaccionAEliminar(null)}
        />
      )}
    </div>
  );
}
